import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, getToken, setToken, TOKEN_KEY } from "@/lib/api";
import { isAuthReloadSuppressed } from "@/lib/authSync";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const qc = useQueryClient();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastUserId = useRef(null);

  useEffect(() => {
    const t = getToken();
    if (!t) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  // Identity changed (login as someone else, token swapped in another tab, …) → never serve another user's cached data.
  useEffect(() => {
    const id = user?.id || null;
    if (lastUserId.current && id && lastUserId.current !== id) qc.clear();
    lastUserId.current = id;
  }, [user?.id, qc]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== TOKEN_KEY || e.oldValue === e.newValue) return;
      // Offentlege sider som berre viser systemet i rammer (tilbodet) skal ikkje hoppe når ramma loggar inn.
      if (isAuthReloadSuppressed()) return;
      window.location.reload();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const login = useCallback(
    async (email, password) => {
      const r = await api.post("/auth/login", { email, password });
      qc.clear();
      setToken(r.data.token);
      setUser(r.data.user);
      return r.data.user;
    },
    [qc]
  );

  const logout = useCallback(() => {
    qc.clear();
    setToken(null);
    setUser(null);
    window.location.href = "/login";
  }, [qc]);

  const can = useCallback(
    (perm) => {
      if (!user) return false;
      const perms = user.permissions || [];
      if (perms.includes("*")) return true;
      if (perms.includes(perm)) return true;
      const res = perm.split(":")[0];
      if (perms.includes(`${res}:*`)) return true;
      if (perm.endsWith(":read") && perms.includes(`${res}:write`)) return true;
      return false;
    },
    [user]
  );

  const value = useMemo(() => ({ user, loading, login, logout, can, isField: user?.role === "felt" }), [user, loading, login, logout, can]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
